import {
  Box,
  Heading,
  Stack,
  Link as ChakraLink,
  Accordion,
  Text,
  List,
  Span,
} from '@chakra-ui/react';

const Documentation = () => {
  return (
    <Stack
      w='full'
      minHeight='content-height'
      overflow='auto'
      paddingTop={{ base: '4', md: '10' }}
      paddingBottom={{ base: '6', md: '16' }}
      paddingInline={{ base: 0, md: '8', lg: '12' }}
    >
      <Box>
        <Heading as='h1' size='2xl' mb='6'>
          Documentation
        </Heading>

        <Stack direction='row' gap='6' mb='8'>
          <ChakraLink
            href='/SKSIS_User_Manual.pdf'
            download
            fontSize='lg'
            fontWeight='semibold'
            color='blue.500'
            _hover={{ color: 'blue.600' }}
          >
            User Manual
          </ChakraLink>
          <ChakraLink
            href='/SKSIS_release_notes.pdf'
            download
            fontSize='lg'
            fontWeight='semibold'
            color='blue.500'
            _hover={{ color: 'blue.600' }}
          >
            Release Notes
          </ChakraLink>
        </Stack>

        <Accordion.Root defaultValue={['map-guide']} multiple variant='enclosed' bg='white'>
          <Accordion.Item value='map-guide'>
            <Accordion.ItemTrigger>
              <Span flex='1' fontSize='lg'>
                Map guide
              </Span>
              <Accordion.ItemIndicator />
            </Accordion.ItemTrigger>
            <Accordion.ItemContent bg='white'>
              <Accordion.ItemBody>
                <Stack gap='4'>
                  <Box>
                    <Heading as='h2' size='2xl' mb='2'>
                      Toolbox
                    </Heading>

                    <Heading as='h4' size='md' mb='2'>
                      Basemap
                    </Heading>
                    <Box mb='3'>
                      <Text fontSize='sm'>
                        The basemap dropdown allows the selection of the source of the underlying
                        imagery on the map. Only one basemap can be active at a time.
                      </Text>
                      <Text fontSize='sm'>
                        Four basemap options are available. They are the Hybrid, Roads, Aerial, or
                        Dark.
                      </Text>
                    </Box>

                    <Heading as='h5' size='sm' mb='1'>
                      Hybrid
                    </Heading>
                    <Box mb='2'>
                      <Text fontSize='sm'>
                        The Hybrid basemap contains political features like cities and roads on top
                        of a satellite imagery layer
                      </Text>
                    </Box>

                    <Heading as='h5' size='sm' mb='1'>
                      Roads
                    </Heading>
                    <Box mb='2'>
                      <Text fontSize='sm'>
                        The Roads basemap contains political boundaries and features, as well as
                        basic geographic features such as lakes, rivers, and wetlands.
                      </Text>
                    </Box>

                    <Heading as='h5' size='sm' mb='1'>
                      Aerial
                    </Heading>
                    <Box mb='2'>
                      <Text fontSize='sm'>
                        The Aerial basemap is a pure satellite layer with no political features.
                      </Text>
                    </Box>

                    <Heading as='h5' size='sm' mb='1'>
                      Dark
                    </Heading>
                    <Box mb='3'>
                      <Text fontSize='sm'>
                        The Dark basemap is a non-satellite imagery layer with very dark styling.
                        This layer can provide better contrast with the lightly coloured soil
                        polygons, particularly when a query result contains a small number of soil
                        polygons.
                      </Text>
                    </Box>
                  </Box>

                  <Box>
                    <Heading as='h4' size='md' mb='2'>
                      Theme
                    </Heading>
                    <Box mb='3'>
                      <Text fontSize='sm'>
                        The theme dropdown changes the styling of the soil polygons based on
                        predefined categories.
                      </Text>
                      <Text fontSize='sm'>
                        Six themes are available. They are soil zone, map unit, agricultural
                        capability, texture, salinity effect on production, and pH.
                      </Text>
                    </Box>
                  </Box>

                  <Box>
                    <Heading as='h4' size='md' mb='2'>
                      Adjust polygon transparency (Opacity)
                    </Heading>
                    <Box mb='3'>
                      <Text fontSize='sm'>
                        The Soil polygon opacity can be adjusted using the slider to allow a view of
                        the Basemap. Adjusting the opacity of the polygons can allow inspection of
                        the soil boundaries with geographic context provided by the also visible
                        basemap.
                      </Text>
                    </Box>
                  </Box>

                  <Box>
                    <Heading as='h4' size='md' mb='2'>
                      Layers
                    </Heading>
                    <Box mb='3'>
                      <Text fontSize='sm'>
                        The layers section is used to turn the display of each layer on and off by
                        the use of the check boxes.
                      </Text>
                    </Box>
                  </Box>

                  <Box>
                    <Heading as='h4' size='md' mb='2'>
                      Point Datatypes
                    </Heading>
                    <Box mb='3'>
                      <Text fontSize='sm'>
                        A variety of point datatypes are stored by SKSIS. They are photos, soil
                        pits, publications, and observations. By checking the associated box, map
                        indicators of the presence of each point datatype will be toggled on or off.
                      </Text>
                    </Box>
                  </Box>

                  <Box>
                    <Heading as='h2' size='2xl' mb='2'>
                      Querybox
                    </Heading>

                    <Heading as='h5' size='sm' mb='2'>
                      Filtering
                    </Heading>
                    <Box mb='3'>
                      <Text fontSize='sm'>
                        The filtering tools allow the selection and display of polygons that meet
                        certain criteria specified with the dropdown menus. SKSIS allows querying on
                        dominant slope class, dominant slope length, salinity class, irrigation
                        potential, surface texture, secondary texture, pH, past erosion class,
                        stoniness class, and map unit.
                      </Text>
                    </Box>

                    <Heading as='h5' size='sm' mb='2'>
                      Searching
                    </Heading>
                    <Box mb='3'>
                      <Text fontSize='sm'>
                        The search tools allow one to center the map to certain geographic locations
                        without scrolling. The type of search tab is selected and then the requested
                        information can then be selected or entered to perform the search.
                      </Text>
                    </Box>
                  </Box>

                  <Box>
                    <Heading as='h2' size='2xl' mb='2'>
                      Soil Info Box
                    </Heading>
                    <Box mb='3'>
                      <Text fontSize='sm'>
                        The soil infobox provides on the fly information such as the latitude and
                        longitude of a click on the map, the latitude and longitude of the mouse on
                        the map, and the current scale of the map.
                      </Text>
                      <Text fontSize='sm'>
                        Upon clicking on a polygon, the soil info box will display information
                        related to that polygon and its components. When the section lines are
                        visible on the screen the legal land description is also provided.
                      </Text>
                    </Box>
                  </Box>
                </Stack>
              </Accordion.ItemBody>
            </Accordion.ItemContent>
          </Accordion.Item>

          <Accordion.Item value='data-collection-guide'>
            <Accordion.ItemTrigger>
              <Span flex='1' fontSize='lg'>
                Data collection guide
              </Span>
              <Accordion.ItemIndicator />
            </Accordion.ItemTrigger>
            <Accordion.ItemContent bg='white'>
              <Accordion.ItemBody>
                <Stack gap='4'>
                  <Box>
                    <Heading as='h2' size='2xl' mb='2'>
                      Adding your data to the map
                    </Heading>
                    <Box mb='3'>
                      <Text fontSize='sm' mb='2'>
                        You can upload your own soil data to SKSIS. We accept photos of soils and
                        landscapes, georeferenced publications, soil pit descriptions, and
                        observations.
                      </Text>
                      <Text fontSize='sm' mb='2'>
                        Observations are simple, short descriptions of an interesting landscape or
                        soil property at a particular location.
                      </Text>
                      <Text fontSize='sm'>
                        The steps below give an example of uploading a photo.
                      </Text>
                    </Box>

                    <List.Root as='ol' fontSize='sm' mb='2' pl='4' ml='4'>
                      <List.Item>Select the "Add Data" menu option</List.Item>
                      <List.Item>
                        If you are not logged in already a login will be required
                      </List.Item>
                      <List.Item>
                        You will now have a series of headings (tabs) at the top to select the type
                        of data you want to add
                      </List.Item>
                      <List.Item>Select the datatype you wish to enter</List.Item>
                      <List.Item>
                        You will then have a form to enter location and description information
                      </List.Item>
                      <List.Item>
                        For file upload types click "Choose file" and find the image on your
                        computer
                      </List.Item>
                      <List.Item>
                        If it is a photo that has a location in exif it will obtain the location
                        information from there
                      </List.Item>
                      <List.Item>Click "Save" to save your entry or "Discard" to restart</List.Item>
                      <List.Item>
                        Below the entry screen you will be able to see all your previous entries and
                        have the option to "Edit" and "Delete" them
                      </List.Item>
                      <List.Item>
                        Selecting "Edit" allows editing and the edit can either be saved with "Save"
                        button or not saved with "Cancel" button
                      </List.Item>
                    </List.Root>
                  </Box>
                </Stack>
              </Accordion.ItemBody>
            </Accordion.ItemContent>
          </Accordion.Item>

          <Accordion.Item value='user-registration-guide'>
            <Accordion.ItemTrigger>
              <Span flex='1' fontSize='lg'>
                User registration guide
              </Span>
              <Accordion.ItemIndicator />
            </Accordion.ItemTrigger>
            <Accordion.ItemContent bg='white'>
              <Accordion.ItemBody>
                <Text>Under construction</Text>
              </Accordion.ItemBody>
            </Accordion.ItemContent>
          </Accordion.Item>
        </Accordion.Root>
      </Box>
    </Stack>
  );
};

export default Documentation;
